/**
 * Registre central des routes admin : sidebar, RBAC, titres, redirects legacy.
 * Une entrée navigation = une permission métier (sauf routes techniques matchées par préfixe).
 */
import type { LucideIcon } from "lucide-react";
import {
  Activity,
  BadgeDollarSign,
  Briefcase,
  Building2,
  FileStack,
  FileText,
  History,
  Inbox,
  Layers,
  LayoutGrid,
  Lock,
  Map as MapIcon,
  MapPin,
  Package,
  Receipt,
  Route,
  Settings,
  Truck,
  User as UserIcon,
  Users,
  Wallet,
  Wrench,
} from "lucide-react";
import type { User, UserRole } from "../context/AuthContext";

// ─── Sections sidebar ─────────────────────────────────────────────

export type AdminSectionId =
  | "PILOTAGE"
  | "CLIENTS"
  | "TERRAIN"
  | "FINANCE"
  | "RESSOURCES"
  | "CONFIGURATION";

export const ADMIN_SECTION_LABELS: Record<AdminSectionId, string> = {
  PILOTAGE: "Pilotage",
  CLIENTS: "Clients",
  TERRAIN: "Terrain",
  FINANCE: "Finance",
  RESSOURCES: "Ressources",
  CONFIGURATION: "Configuration",
};

// ─── Permissions ─────────────────────────────────────────────────

export type AdminPermissionKey =
  | "dashboard.view"
  | "clients.view"
  | "abonnements.view"
  | "plans.view"
  | "portailClient.view"
  | "organisationTerrain.view"
  | "passages.view"
  | "tournees.view"
  | "executionPassages.view"
  | "historiquePassages.view"
  | "carteTerrain.view"
  | "factures.view"
  | "paiements.view"
  | "recouvrement.view"
  | "proformas.view"
  | "quittances.view"
  | "clotures.view"
  | "agents.view"
  | "agentsTerrain.view"
  | "agentsTerrain.manage"
  | "agentsTerrain.assign"
  | "rhEmployes.view"
  | "rhEmployes.manage"
  | "rhEmployes.documents"
  | "rhEmployes.payroll"
  | "ressources.view"
  | "documentsCommerciaux.manage"
  | "parametresCommerciaux.view"
  | "geographie.view"
  | "geographie.manage"
  | "zones.view"
  | "zones.manage"
  | "zones.delete"
  | "adminUsers.view";

/** Rôles staff autorisés à accéder au shell /admin (hors CLIENT). */
export const ADMIN_STAFF_ROLES: UserRole[] = [
  "admin",
  "superviseur",
  "agent",
  "planificateur",
  "chef",
  "portier",
  "recouvrement",
  "mairie",
  "finance",
];

/**
 * Matrice minimale par rôle (complétable par API user.permissions).
 * `admin` : toutes les clés (voir hasPermission).
 */
const ROLE_PERMISSIONS: Partial<Record<UserRole, readonly AdminPermissionKey[]>> = {
  admin: [
    "dashboard.view",
    "clients.view",
    "abonnements.view",
    "plans.view",
    "portailClient.view",
    "organisationTerrain.view",
    "passages.view",
    "tournees.view",
    "executionPassages.view",
    "historiquePassages.view",
    "carteTerrain.view",
    "factures.view",
    "paiements.view",
    "recouvrement.view",
    "proformas.view",
    "quittances.view",
    "clotures.view",
    "agents.view",
    "agentsTerrain.view",
    "agentsTerrain.manage",
    "agentsTerrain.assign",
    "rhEmployes.view",
    "rhEmployes.manage",
    "rhEmployes.documents",
    "rhEmployes.payroll",
    "ressources.view",
    "documentsCommerciaux.manage",
    "parametresCommerciaux.view",
    "geographie.view",
    "geographie.manage",
    "zones.view",
    "zones.manage",
    "zones.delete",
    "adminUsers.view",
  ],
  planificateur: [
    "dashboard.view",
    "clients.view",
    "abonnements.view",
    "plans.view",
    "organisationTerrain.view",
    "geographie.view",
    "zones.view",
    "zones.manage",
    "passages.view",
    "tournees.view",
    "executionPassages.view",
    "historiquePassages.view",
    "carteTerrain.view",
    "factures.view",
    "proformas.view",
    "quittances.view",
  ],
  superviseur: [
    "dashboard.view",
    "clients.view",
    "organisationTerrain.view",
    "geographie.view",
    "zones.view",
    "zones.manage",
    "passages.view",
    "tournees.view",
    "executionPassages.view",
    "historiquePassages.view",
    "carteTerrain.view",
    "agents.view",
    "ressources.view",
  ],
  chef: [
    "dashboard.view",
    "organisationTerrain.view",
    "geographie.view",
    "zones.view",
    "passages.view",
    "tournees.view",
    "executionPassages.view",
    "historiquePassages.view",
    "carteTerrain.view",
    "agents.view",
  ],
  agent: ["dashboard.view", "executionPassages.view", "historiquePassages.view", "carteTerrain.view"],
  recouvrement: [
    "dashboard.view",
    "clients.view",
    "factures.view",
    "paiements.view",
    "recouvrement.view",
    "proformas.view",
    "quittances.view",
  ],
  finance: [
    "dashboard.view",
    "factures.view",
    "paiements.view",
    "recouvrement.view",
    "proformas.view",
    "quittances.view",
    "clotures.view",
    "parametresCommerciaux.view",
    "geographie.view",
    "rhEmployes.view",
    "rhEmployes.payroll",
  ],
  mairie: ["dashboard.view", "clients.view", "carteTerrain.view"],
  portier: ["dashboard.view", "passages.view", "historiquePassages.view"],
};

const ALL_ADMIN_PERMISSION_KEYS_LIST: AdminPermissionKey[] = [
  "dashboard.view",
  "clients.view",
  "abonnements.view",
  "plans.view",
  "portailClient.view",
  "organisationTerrain.view",
  "passages.view",
  "tournees.view",
  "executionPassages.view",
  "historiquePassages.view",
  "carteTerrain.view",
  "factures.view",
  "paiements.view",
  "recouvrement.view",
  "proformas.view",
  "quittances.view",
  "clotures.view",
  "agents.view",
  "agentsTerrain.view",
  "agentsTerrain.manage",
  "agentsTerrain.assign",
  "rhEmployes.view",
  "rhEmployes.manage",
  "rhEmployes.documents",
  "rhEmployes.payroll",
  "ressources.view",
  "documentsCommerciaux.manage",
  "parametresCommerciaux.view",
  "geographie.view",
  "geographie.manage",
  "zones.view",
  "zones.manage",
  "zones.delete",
  "adminUsers.view",
];

/** Vérifie une permission (API future : user.permissions.includes(key)). */
export function hasPermission(user: User | null, key: AdminPermissionKey): boolean {
  if (!user) return false;
  const r = String(user.role).toLowerCase() as UserRole;
  if (r === "client") return false;
  if (r === "admin") return true;

  const fromApi = (user as User & { permissions?: string[] }).permissions;
  if (Array.isArray(fromApi) && fromApi.includes(key)) return true;

  const list = ROLE_PERMISSIONS[r];

  /** Agents terrain : clés métiers + rétrocompat `agents.view`. */
  if (
    key === "agentsTerrain.view" ||
    key === "agentsTerrain.manage" ||
    key === "agentsTerrain.assign"
  ) {
    return Array.isArray(list) ? list.includes("agents.view") : false;
  }

  /** Employés RH : pas dans la matrice par défaut hors admin — délégué à permissions API fines. */

  return Array.isArray(list) ? list.includes(key) : false;
}

// ─── Entrées navigation (sidebar) ─────────────────────────────────

export type AdminNavEntry = {
  key: string;
  label: string;
  path: string;
  section: AdminSectionId;
  icon: LucideIcon;
  permissionKey: AdminPermissionKey;
  /** Si false, seul match exact (ex. dashboard). */
  matchPrefix?: boolean;
};

export const ADMIN_NAV_ENTRIES: AdminNavEntry[] = [
  {
    key: "dashboard",
    label: "Tableau de bord",
    path: "/admin/dashboard",
    section: "PILOTAGE",
    icon: LayoutGrid,
    permissionKey: "dashboard.view",
    matchPrefix: false,
  },
  {
    key: "clients",
    label: "Clients",
    path: "/admin/clients",
    section: "CLIENTS",
    icon: Users,
    permissionKey: "clients.view",
  },
  {
    key: "abonnements",
    label: "Abonnements",
    path: "/admin/abonnements",
    section: "CLIENTS",
    icon: FileText,
    permissionKey: "abonnements.view",
  },
  {
    key: "plans",
    label: "Plans d’abonnement",
    path: "/admin/plans-abonnement",
    section: "CLIENTS",
    icon: Layers,
    permissionKey: "plans.view",
  },
  {
    key: "portail",
    label: "Portail client",
    path: "/admin/portail-client",
    section: "CLIENTS",
    icon: Building2,
    permissionKey: "portailClient.view",
  },
  {
    key: "demandesClient",
    label: "Demandes client",
    path: "/admin/demandes-client",
    section: "CLIENTS",
    icon: Inbox,
    permissionKey: "clients.view",
  },
  {
    key: "organisation",
    label: "Organisation terrain",
    path: "/admin/organisation-terrain",
    section: "TERRAIN",
    icon: MapPin,
    permissionKey: "organisationTerrain.view",
    matchPrefix: false,
  },
  {
    key: "zones",
    label: "Zones opérationnelles",
    path: "/admin/zones",
    section: "TERRAIN",
    icon: MapPin,
    permissionKey: "zones.view",
    matchPrefix: false,
  },
  {
    key: "passages",
    label: "Passages",
    path: "/admin/passages",
    section: "TERRAIN",
    icon: Route,
    permissionKey: "passages.view",
  },
  {
    key: "auditOperational",
    label: "Audit opérationnel",
    path: "/admin/audit-operational",
    section: "TERRAIN",
    icon: History,
    permissionKey: "executionPassages.view",
    matchPrefix: false,
  },
  {
    key: "businessEvents",
    label: "Événements métier",
    path: "/admin/business-events",
    section: "TERRAIN",
    icon: Activity,
    permissionKey: "executionPassages.view",
    matchPrefix: false,
  },
  {
    key: "tournees",
    label: "Tournées",
    path: "/admin/tournees",
    section: "TERRAIN",
    icon: Truck,
    permissionKey: "tournees.view",
  },
  {
    key: "execution",
    label: "Exécution passages",
    path: "/admin/execution-passages",
    section: "TERRAIN",
    icon: Wrench,
    permissionKey: "executionPassages.view",
    matchPrefix: false,
  },
  {
    key: "historique",
    label: "Historique passages",
    path: "/admin/historique-passages",
    section: "TERRAIN",
    icon: History,
    permissionKey: "historiquePassages.view",
  },
  {
    key: "carte",
    label: "Carte terrain",
    path: "/admin/carte-terrain",
    section: "TERRAIN",
    icon: MapIcon,
    permissionKey: "carteTerrain.view",
    matchPrefix: false,
  },
  {
    key: "factures",
    label: "Factures",
    path: "/admin/finance/factures",
    section: "FINANCE",
    icon: FileText,
    permissionKey: "factures.view",
  },
  {
    key: "paiements",
    label: "Paiements",
    path: "/admin/finance/paiements",
    section: "FINANCE",
    icon: Wallet,
    permissionKey: "paiements.view",
  },
  {
    key: "recouvrement",
    label: "Recouvrement",
    path: "/admin/finance/recouvrement",
    section: "FINANCE",
    icon: BadgeDollarSign,
    permissionKey: "recouvrement.view",
  },
  {
    key: "proformas",
    label: "Proformas",
    path: "/admin/finance/proformas",
    section: "FINANCE",
    icon: FileStack,
    permissionKey: "proformas.view",
  },
  {
    key: "quittances",
    label: "Quittances",
    path: "/admin/finance/quittances",
    section: "FINANCE",
    icon: Receipt,
    permissionKey: "quittances.view",
  },
  {
    key: "clotures",
    label: "Clôtures",
    path: "/admin/finance/clotures",
    section: "FINANCE",
    icon: Lock,
    permissionKey: "clotures.view",
  },
  {
    key: "agents",
    label: "Agents terrain",
    path: "/admin/agents",
    section: "RESSOURCES",
    icon: UserIcon,
    permissionKey: "agentsTerrain.view",
  },
  {
    key: "rhEmployes",
    label: "Employés RH",
    path: "/admin/rh/employes",
    section: "RESSOURCES",
    icon: Briefcase,
    permissionKey: "rhEmployes.view",
  },
  {
    key: "ressources",
    label: "Ressources opérationnelles",
    path: "/admin/ressources-operationnelles",
    section: "RESSOURCES",
    icon: Package,
    permissionKey: "ressources.view",
  },
  {
    key: "ressourcesCatalogue",
    label: "Catalogue ressources",
    path: "/admin/ressources/catalogue",
    section: "RESSOURCES",
    icon: Layers,
    permissionKey: "ressources.view",
  },
  {
    key: "ressourcesMouvementsCatalog",
    label: "Mouvements stock (catalogue)",
    path: "/admin/ressources/mouvements",
    section: "RESSOURCES",
    icon: History,
    permissionKey: "ressources.view",
  },
  {
    key: "utilisateurs",
    label: "Utilisateurs",
    path: "/admin/utilisateurs",
    section: "CONFIGURATION",
    icon: UserIcon,
    permissionKey: "adminUsers.view",
    matchPrefix: true,
  },
  {
    key: "parametres",
    label: "Paramètres",
    path: "/admin/parametres",
    section: "CONFIGURATION",
    icon: Settings,
    permissionKey: "parametresCommerciaux.view",
    matchPrefix: true,
  },
  {
    key: "geographie",
    label: "Géographie",
    path: "/admin/geographie",
    section: "CONFIGURATION",
    icon: MapPin,
    permissionKey: "geographie.view",
    matchPrefix: false,
  },
];

/** Préfixes techniques / hors menu : même permission que la section parente pour le guard. */
export const ADMIN_ROUTE_PREFIX_FALLBACK: { prefix: string; permissionKey: AdminPermissionKey }[] = [
  { prefix: "/admin/rh", permissionKey: "rhEmployes.view" },
  { prefix: "/admin/contrats", permissionKey: "abonnements.view" },
  { prefix: "/admin/categories-tarifs", permissionKey: "plans.view" },
  { prefix: "/admin/equipes", permissionKey: "organisationTerrain.view" },
  { prefix: "/admin/planificateur", permissionKey: "passages.view" },
  { prefix: "/admin/interventions", permissionKey: "executionPassages.view" },
  { prefix: "/admin/paiements", permissionKey: "paiements.view" },
  { prefix: "/admin/recouvrement", permissionKey: "recouvrement.view" },
  { prefix: "/admin/terrain/carte", permissionKey: "carteTerrain.view" },
  { prefix: "/admin/passages/historique", permissionKey: "historiquePassages.view" },
  { prefix: "/admin/passages/urgence", permissionKey: "passages.view" },
  { prefix: "/admin/map", permissionKey: "carteTerrain.view" },
  { prefix: "/admin/utilisateurs", permissionKey: "adminUsers.view" },
];

export type AdminSidebarSection = { title: string; items: { label: string; to: string; icon: LucideIcon }[] };

export function getAdminSidebarSections(user: User | null): AdminSidebarSection[] {
  if (!user || String(user.role).toLowerCase() === "client") return [];

  const sectionOrder: AdminSectionId[] = [
    "PILOTAGE",
    "CLIENTS",
    "TERRAIN",
    "FINANCE",
    "RESSOURCES",
    "CONFIGURATION",
  ];

  const bySection = new Map<AdminSectionId, AdminSidebarSection["items"]>();
  for (const id of sectionOrder) {
    bySection.set(id, []);
  }

  for (const entry of ADMIN_NAV_ENTRIES) {
    if (!hasPermission(user, entry.permissionKey)) continue;
    const items = bySection.get(entry.section)!;
    items.push({ label: entry.label, to: entry.path, icon: entry.icon });
  }

  return sectionOrder
    .map((id) => ({
      title: ADMIN_SECTION_LABELS[id],
      items: bySection.get(id) ?? [],
    }))
    .filter((s) => s.items.length > 0);
}

/** Active sidebar : évite double actif après redirect (chemins canoniques uniquement). */
export function isAdminSidebarActive(pathname: string, itemPath: string, matchPrefix = true): boolean {
  if (!matchPrefix || itemPath === "/admin/dashboard") {
    return pathname === itemPath;
  }
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

/** Pour NavLink : préfixe selon l’entrée registre. */
export function getNavMatchPrefix(itemPath: string): boolean {
  const entry = ADMIN_NAV_ENTRIES.find((e) => e.path === itemPath);
  return entry?.matchPrefix !== false;
}

function normalizePathForMatch(pathname: string): string {
  let p = pathname.split("?")[0] ?? pathname;
  if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
  return p;
}

/** Trouve l’entrée menu correspondante (pour titre / guard). */
export function matchAdminNavEntry(pathname: string): AdminNavEntry | undefined {
  const p = normalizePathForMatch(pathname);
  const sorted = [...ADMIN_NAV_ENTRIES].sort((a, b) => b.path.length - a.path.length);
  for (const entry of sorted) {
    const usePrefix = entry.matchPrefix !== false;
    if (!usePrefix && p === entry.path) return entry;
    if (usePrefix && (p === entry.path || p.startsWith(`${entry.path}/`))) return entry;
  }
  return undefined;
}

export function resolvePermissionForPath(pathname: string): AdminPermissionKey | undefined {
  const p = normalizePathForMatch(pathname);
  /** Sous-routes paramètres : permissions métier plus fines que le hub. */
  if (p.startsWith("/admin/parametres/documents-commerciaux")) {
    return "documentsCommerciaux.manage";
  }
  if (p.startsWith("/admin/parametres/plans-abonnement")) {
    return "plans.view";
  }

  const direct = matchAdminNavEntry(pathname);
  if (direct) return direct.permissionKey;

  const fallbacks = [...ADMIN_ROUTE_PREFIX_FALLBACK].sort((a, b) => b.prefix.length - a.prefix.length);
  for (const { prefix, permissionKey } of fallbacks) {
    if (p === prefix || p.startsWith(`${prefix}/`)) return permissionKey;
  }
  return undefined;
}

export function canAccessAdminPath(user: User | null, pathname: string): boolean {
  if (!user) return false;
  const role = String(user.role).toLowerCase() as UserRole;
  if (role === "client") return false;

  const key = resolvePermissionForPath(pathname);
  if (!key) {
    /** Routes /admin non répertoriées : autoriser le staff authentifié (compat legacy). */
    return ADMIN_STAFF_ROLES.includes(role);
  }
  return hasPermission(user, key);
}

/** Titres header shell */
export function getAdminPageTitle(pathname: string): string {
  const p = normalizePathForMatch(pathname);
  if (p.startsWith("/admin/parametres/documents-commerciaux")) return "Documents commerciaux";
  if (p.startsWith("/admin/parametres/plans-abonnement")) return "Plans d’abonnement";

  if (p === "/admin/clients/nouveau") return "Nouveau client";

  if (p === "/admin/utilisateurs/nouveau") return "Nouvel utilisateur";
  if (p.startsWith("/admin/utilisateurs") && /^\/admin\/utilisateurs\/\d+$/.test(p))
    return "Utilisateur";

  if (p === "/admin/rh/employes/nouveau") return "Nouvel employé RH";
  if (p.startsWith("/admin/rh/employes") && /^\/admin\/rh\/employes\/\d+$/.test(p)) return "Employé RH";
  if (p.startsWith("/admin/rh/employes")) return "Employés RH";

  if (p.startsWith("/admin/agents") && /^\/admin\/agents\/\d+$/.test(p))
    return "Fiche agent terrain";

  const entry = matchAdminNavEntry(pathname);
  if (entry) return entry.label;

  if (p === "/admin/finance/clotures") return "Clôtures financières";

  if (p.startsWith("/admin/finance/factures") && p.includes("/nouvelle")) return "Nouvelle facture";
  if (p.includes("/finance/factures/") && /\/admin\/finance\/factures\/\d+/.test(p)) return "Facture";
  if (p.startsWith("/admin/recouvrement/ajouter")) return "Recouvrement";

  const titles: [string, string][] = [
    ["/admin/demandes-client", "Demandes client"],
    ["/admin/contrats", "Abonnements"],
    ["/admin/categories-tarifs", "Plans d’abonnement"],
    ["/admin/planificateur", "Planification"],
    ["/admin/equipes", "Équipes"],
    ["/admin/planificateur/historique", "Historique passages"],
  ];
  for (const [prefix, title] of titles) {
    if (p.startsWith(prefix)) return title;
  }
  return "Pilotage métier";
}

// ─── Redirects legacy (centralisés) ─────────────────────────────

export const ADMIN_LEGACY_REDIRECTS: readonly { from: string; to: string }[] = [
  { from: "/admin/interventions", to: "/admin/execution-passages" },
  { from: "/admin/planificateur/repartition", to: "/admin/organisation-terrain" },
  { from: "/admin/planificateur/historique", to: "/admin/historique-passages" },
  { from: "/admin/charroi/vehicules", to: "/admin/ressources-operationnelles" },
  { from: "/admin/espace-client-info", to: "/admin/portail-client" },
  { from: "/admin/zones-operationnelles", to: "/admin/zones" },
] as const;

export function findLegacyRedirect(fromPathname: string): string | undefined {
  const p = normalizePathForMatch(fromPathname);
  const rule = ADMIN_LEGACY_REDIRECTS.find((r) => p === r.from);
  return rule?.to;
}

/** Export liste complète des permission keys (tests / doc). */
export function getAllAdminPermissionKeys(): readonly AdminPermissionKey[] {
  return ALL_ADMIN_PERMISSION_KEYS_LIST;
}
